<!-- A note as a LINE, rather than as a mark in the margin.
     The margin form (MarginNote) is for reading: a mark, no label, no
     controls, and a press-and-hold that teaches nobody. That is right on the
     page you cook from, which has to keep looking like paper.

     It is wrong everywhere you are changing things. Four unlabelled red discs
     in a row is a puzzle — you cannot tell which is which, whose voice each
     one is, or how to move one that landed on the wrong step — and the only
     way to act on one was a gesture nothing announced. So editing and capture
     list them instead: the same disc to play, what the note IS in words, and a
     menu mark opening the same actions the hold gesture opens.

     ONE RULED LINE TALL, so a list of notes costs the lines it uses and not a
     pixel more. Everything that needs a 44px target gets it from an overlay
     rather than from padding, because padding would make the control taller
     than the line it sits on — the exact mistake that once made every step
     occupy two ruled lines. -->
<script setup>
import { computed } from 'vue';
import HdIcon from './HdIcon.vue';
import { isAudible, noteLabel } from '../lib/notes.js';

const props = defineProps({
  /** { id, kind, body, mediaUrl, durationSeconds } */
  note: { type: Object, required: true },
  cookName: { type: String, default: '' },
  playing: { type: Boolean, default: false },
});

defineEmits(['play', 'manage']);

/* One definition of each, in lib/notes.js. This component used to carry its
   own `kind !== 'written'`, which called a photograph audible. */
const audible = computed(() => isAudible(props.note));
const label = computed(() => noteLabel(props.note, props.cookName));
</script>

<template>
  <li class="note-row">
    <button
      class="note-play"
      type="button"
      :aria-label="playing ? 'Stop' : (audible ? `Play ${label}` : `Read ${label}`)"
      @click="$emit('play', note.id)"
    >
      <span
        class="hd-disc"
        :class="{ 'hd-disc-written': !audible }"
        :data-playing="playing ? 'true' : 'false'"
      >
        <HdIcon v-if="!audible" name="lines" :size="10" :weight="1.9" />
        <HdIcon v-else :name="playing ? 'pause' : 'play'" :size="9" />
      </span>
    </button>

    <span class="note-label">{{ label }}</span>

    <!-- The same list the press-and-hold opens, behind a mark you can see.
         The gesture stays for the sheet; nobody should have to know it to move
         a note off the wrong step.

         A pencil rather than three dots, matching every other "change this"
         mark on the page. It is the loosest fit of the three — this list is
         play, move and delete rather than rewrite — but one mark meaning
         "act on this line" beats two marks the reader has to tell apart. -->
    <button
      class="note-menu"
      type="button"
      aria-label="What to do with this note"
      aria-haspopup="true"
      @click="$emit('manage', note.id)"
    >
      <HdIcon name="pencil" :size="15" />
    </button>
  </li>
</template>

<style scoped>
.note-row {
  display: flex;
  align-items: center;
  gap: 10px;
  height: var(--hd-rule);
  position: relative;
  padding-right: 34px;
}

.note-play {
  position: relative;
  display: flex;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
}
/* The target, as an overlay. 24px of disc is not a tap target and 44px of
   padding is not a 28px line, so the hit area is drawn over both. */
.note-play::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 44px;
  height: 44px;
  transform: translate(-50%, -50%);
}

.note-label {
  flex-grow: 1;
  min-width: 0;
  font-family: var(--hd-sans);
  /* A note's label is read, not consulted — it is whose voice this is, which
     is the thing that tells two recordings on one step apart. --hd-t-figure
     is for numbers. */
  font-size: var(--hd-t-small);
  color: var(--hd-pencil);
  line-height: var(--hd-rule);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-menu {
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: 0;
  color: var(--hd-pencil);
  cursor: pointer;
}
.note-menu:hover,
.note-menu:focus-visible { color: var(--hd-margin); }

.note-play:focus-visible,
.note-menu:focus-visible {
  outline: 2px solid var(--hd-focus);
  outline-offset: 2px;
}
</style>
